import type { ReactNode } from 'react';
import { useQxoFocus } from '@/components/qxo/qxoFocus';

/** Shared visual primitives for the QXO desk boards (roofing market, filings, street). */

export const LEGEND = { fontSize: 10, color: '#bae6fd' } as const;

export const TONE: Record<string, string> = {
  danger: 'text-rose-300',
  warning: 'text-amber-300',
  neutral: 'text-sky-100',
  success: 'text-emerald-300',
};

export type Tone = keyof typeof TONE;

export interface KpiItem {
  id: string;
  label: string;
  value: string;
  hint: string;
  tone: Tone;
}

export function signedPct(n: number, digits = 1): string {
  const t = Number.parseFloat(n.toFixed(digits)).toString();
  if (n > 0) return `+${t}%`;
  if (n < 0) return `${t}%`;
  return '0%';
}

export function fmtMoneyM(n: number | null | undefined, digits = 0): string {
  if (n == null || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const body =
    abs >= 1000
      ? `$${(abs / 1000).toFixed(digits === 0 ? 2 : digits)}B`
      : `$${abs.toFixed(digits)}M`;
  return n < 0 ? `(${body})` : body;
}

export function fmtNum(n: number | null | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

export function fmtPct(n: number | null | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

export function ChartTooltip({
  active,
  payload,
  label,
  formatValue,
}: {
  active?: boolean;
  payload?: { name?: string; value?: number | null; color?: string }[];
  label?: string;
  formatValue: (value: number, name: string) => string;
}) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((row) => row.value != null && !Number.isNaN(Number(row.value)));
  if (!rows.length) return null;
  return (
    <div className="rounded-lg border border-sky-800 bg-[#0b2a4a] px-2.5 py-2 text-[11px] text-slate-100 shadow-lg">
      <p className="mb-1 text-sky-300">{label}</p>
      {rows.map((row) => {
        const name = row.name ?? '';
        return (
          <p key={name} className="flex justify-between gap-4 leading-5">
            <span style={{ color: row.color }}>{name}</span>
            <span className="tabular-nums text-white">{formatValue(Number(row.value), name)}</span>
          </p>
        );
      })}
    </div>
  );
}

export function Panel({
  title,
  unit,
  takeaway,
  note,
  children,
  tall,
  fixedHeight = true,
  datasetId,
  overviewOnly,
}: {
  title: string;
  unit: string;
  takeaway: string;
  note?: string;
  children: ReactNode;
  tall?: boolean;
  /** Charts need a fixed-height box; tables should size to content. */
  fixedHeight?: boolean;
  /** Opens the single-dataset view. Panels without an id stay on the overview only. */
  datasetId?: string;
  /** Clickable on the overview, hidden once that dataset is open (the chart panel carries it). */
  overviewOnly?: boolean;
}) {
  const { focusId, open } = useQxoFocus();
  if (focusId && (datasetId !== focusId || overviewOnly)) return null;
  const focused = Boolean(focusId && datasetId === focusId);
  const box = !fixedHeight ? '' : focused ? 'h-[460px]' : tall ? 'h-[280px]' : 'h-[228px]';
  return (
    <article
      className={`flex min-w-0 flex-col rounded-xl border bg-[#0d3358] p-4 ${
        focused ? 'col-span-full border-sky-700' : 'border-sky-900/60'
      } ${datasetId && !focused ? 'cursor-pointer hover:border-sky-600' : ''}`}
      onClick={
        datasetId && !focused
          ? (event) => {
              const target = event.target as HTMLElement;
              if (target.closest('a, button, input, select, textarea')) return;
              open(datasetId);
            }
          : undefined
      }
      onKeyDown={
        datasetId && !focused
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                open(datasetId);
              }
            }
          : undefined
      }
      role={datasetId && !focused ? 'button' : undefined}
      tabIndex={datasetId && !focused ? 0 : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-[14px] font-semibold text-white">{title}</h3>
        {datasetId && !focused ? (
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-sky-400">Full series</span>
        ) : null}
      </div>
      <p className="mt-0.5 text-[10px] uppercase tracking-wide text-sky-400">{unit}</p>
      <p className="mt-2 text-[12px] leading-5 text-sky-100">{takeaway}</p>
      <div className={`mt-3 w-full min-w-0 ${box}`}>{children}</div>
      {note ? <p className="mt-2 text-[11px] leading-4 text-sky-400">{note}</p> : null}
    </article>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  const { focusId } = useQxoFocus();
  if (focusId) return null;
  return (
    <div className="px-5 pb-2 pt-3">
      <p className="text-[11px] uppercase tracking-wide text-sky-400">{title}</p>
      <p className="mt-1 text-[13px] leading-5 text-sky-200">{children}</p>
    </div>
  );
}

export function KpiGrid({ items, columns = 6 }: { items: KpiItem[]; columns?: 4 | 5 | 6 }) {
  const { focusId } = useQxoFocus();
  if (focusId) return null;
  const xl = columns === 4 ? 'xl:grid-cols-4' : columns === 5 ? 'xl:grid-cols-5' : 'xl:grid-cols-6';
  return (
    <div className={`grid min-w-0 grid-cols-2 gap-3 px-4 pb-4 md:grid-cols-3 ${xl}`}>
      {items.map((kpi) => (
        <div key={kpi.id} className="rounded-xl border border-sky-900/60 bg-[#0d3358] px-3 py-3">
          <p className="text-[10px] uppercase tracking-wide text-sky-400">{kpi.label}</p>
          <p className={`mt-1 text-xl font-semibold ${TONE[kpi.tone]}`}>{kpi.value}</p>
          <p className="mt-1 text-[11px] leading-4 text-sky-300">{kpi.hint}</p>
        </div>
      ))}
    </div>
  );
}

export interface DataColumn<Row> {
  key: string;
  header: string;
  align?: 'left' | 'right';
  render: (row: Row) => ReactNode;
  /** Optional per-cell tone class. */
  tone?: (row: Row) => string | undefined;
}

export function DataTable<Row>({
  rows,
  columns,
  rowKey,
  dense,
  highlightRow,
}: {
  rows: Row[];
  columns: DataColumn<Row>[];
  rowKey: (row: Row) => string;
  dense?: boolean;
  highlightRow?: (row: Row) => boolean;
}) {
  const pad = dense ? 'px-2 py-1' : 'px-2 py-1.5';
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[520px] border-collapse text-[11px] tabular-nums">
        <thead>
          <tr className="border-b border-sky-800/80 text-[10px] uppercase tracking-wide text-sky-400">
            {columns.map((col) => (
              <th key={col.key} className={`${pad} font-medium ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const hl = highlightRow?.(row);
            return (
              <tr
                key={rowKey(row)}
                className={`border-b border-sky-900/50 last:border-0 ${hl ? 'bg-[#0a2744] font-semibold text-white' : 'text-sky-100'}`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`${pad} ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.tone?.(row) ?? ''}`}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function NoteList({
  title,
  subtitle,
  items,
  datasetId,
}: {
  title: string;
  subtitle: string;
  items: { head: string; body: string; foot?: string; badge?: string }[];
  datasetId?: string;
}) {
  const { focusId, open } = useQxoFocus();
  if (focusId) return null;
  return (
    <article
      className={`rounded-xl border border-sky-900/60 bg-[#0d3358] p-4 ${datasetId ? 'cursor-pointer hover:border-sky-600' : ''}`}
      onClick={datasetId ? () => open(datasetId) : undefined}
      onKeyDown={
        datasetId
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                open(datasetId);
              }
            }
          : undefined
      }
      role={datasetId ? 'button' : undefined}
      tabIndex={datasetId ? 0 : undefined}
    >
      <h3 className="text-[14px] font-semibold text-white">{title}</h3>
      <p className="mt-1 text-[12px] text-sky-300">{subtitle}</p>
      <ul className="mt-3 space-y-3">
        {items.map((row) => (
          <li key={row.head} className="border-t border-sky-900/70 pt-3 first:border-0 first:pt-0">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[13px] font-medium text-white">{row.head}</p>
              {row.badge ? (
                <span className="shrink-0 rounded-md bg-[#0a2744] px-2 py-0.5 text-[10px] uppercase tracking-wide text-sky-300">
                  {row.badge}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-[12px] leading-5 text-sky-200">{row.body}</p>
            {row.foot ? <p className="mt-1 text-[11px] text-sky-400">{row.foot}</p> : null}
          </li>
        ))}
      </ul>
    </article>
  );
}

export function BoardShell({
  id,
  title,
  subtitle,
  blurb,
  asOf,
  sourceLine,
  children,
  footer,
}: {
  id?: string;
  title: string;
  subtitle: string;
  blurb: string;
  asOf: string;
  sourceLine: string;
  children: ReactNode;
  footer: string;
}) {
  const { focusId } = useQxoFocus();
  if (focusId) {
    return <section className="w-full min-w-0">{children}</section>;
  }
  return (
    <section id={id} className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl bg-[#071f38] text-white">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-sky-900/80 px-5 py-4">
        <div>
          <p className="text-lg font-semibold tracking-tight">
            {title} <span className="font-normal text-sky-200">{subtitle}</span>
          </p>
          <p className="mt-1 max-w-3xl text-[13px] leading-5 text-sky-200">{blurb}</p>
        </div>
        <div className="text-right text-[11px] text-sky-300">
          <p>{asOf}</p>
          <p>{sourceLine}</p>
        </div>
      </header>
      {children}
      <footer className="border-t border-sky-900/80 px-5 py-3 text-[11px] leading-4 text-sky-400">{footer}</footer>
    </section>
  );
}
